import 'swiper/css'
import { useTranslation } from 'next-i18next'
import { Suspense, lazy, memo } from 'react'
import { Fade } from 'react-awesome-reveal'
import { css } from '@emotion/react'

import { Grid } from '@/styles/grid'
import Accordion from '@/components/common/Accordion'
import TextWithButton from '@/components/common/TextWithButton'
import Title from '@/components/common/Title'

import { mediaColumns } from '@/styles/grid'
import { mediaUp } from '@/styles/media'
import { spacingSection, spacingSmallSection } from '@/styles/mixins'

const LazyFaqPriceList = lazy(() => import('./FaqPriceList'))

interface Props {
  category?: string
  slugEn?: string
  faqPage?: boolean
  withoutMargin?: boolean
  dataTestId?: string
  forHomePage?: boolean
  slug?: string
}

function Faq({
  category,
  slugEn,
  faqPage = false,
  withoutMargin = false,
  dataTestId
}: Props) {
  const { t } = useTranslation('common')
  const navipay = slugEn === 'navipay-app'
  const titleId = `faq-title-${dataTestId || 'main'}`

  return (
    <section
      css={[styles.container, withoutMargin && styles.containerNoMargin]}
      data-test-id={dataTestId}
      aria-labelledby={titleId}
    >
      <Fade triggerOnce duration={800}>
        {category ? (
          <Title
            id={titleId}
            text={category}
            primary
            style={{ textAlign: 'center' }}
          />
        ) : (
          <Title id={titleId} text={t('faq.title')} />
        )}
        <Grid>
          <div css={styles.accordion}>
            <Accordion />
            {!faqPage && (
              <Suspense fallback={<div css={styles.priceListSkeleton} />}>
                <LazyFaqPriceList />
              </Suspense>
            )}
          </div>
          {faqPage ? (
            <TextWithButton
              text={t('faq.readyForParking')}
              button={t(navipay ? 'faq.buttonNavipay' : 'faq.buttonSearch')}
              link={{ pathname: navipay ? '/navipay' : '/parking-search' }}
            />
          ) : (
            <TextWithButton
              dataTestId="home-faq-button"
              text={t('faq.buttonMore')}
              button={t('faq.buttonCheck')}
              link={{ pathname: '/faq' }}
            />
          )}
        </Grid>
      </Fade>
    </section>
  )
}

const styles = {
  container: css`
    ${spacingSection};
    contain: content;
  `,
  containerNoMargin: css`
    margin-top: 0;

    ${mediaUp('lg')} {
      margin-top: 0;
    }
  `,
  accordion: css`
    ${mediaColumns({ xs: [12, 0], md: [10, 1] })};
    ${spacingSmallSection};
    contain: layout;
  `,
  priceListSkeleton: css`
    height: 48px;
    background: linear-gradient(
      90deg,
      rgba(240, 240, 240, 0.1) 0%,
      rgba(240, 240, 240, 0.2) 50%,
      rgba(240, 240, 240, 0.1) 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 4px;
    margin-top: 10px;

    @keyframes shimmer {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }
  `
}

export default memo(Faq)
